import { authFetch } from "@/lib/authFetch";
import { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, GripVertical, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors,
  closestCorners, DragOverlay, useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext, useSortable, sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { STAGES } from "./ProjetosTab";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Sessão UI-2 — ProjetoKanban (T5: DnD entre colunas com optimistic UI)
//
// DnD via @dnd-kit/core + @dnd-kit/sortable. Cards são useSortable
// (drag handle no GripVertical via setActivatorNodeRef). Colunas vazias
// são useDroppable pra aceitar drop em coluna sem cards.
//
// Optimistic UI:
//   onDragEnd: detecta stage alvo (over.data.current.stage) e atualiza
//   state local imediatamente; PUT /api/projects/tasks/{id} fire-and-
//   forget. Em erro: reverte estado e toast.
//
// Botão checkbox (next stage) preservado como acessibilidade — keyboard
// users sem mouse podem avançar a task sem DnD.

// KANBAN-UNIFIED-VIEW (3.5) — emoji por tipo de item vinculado ao
// projeto. tasks legacy continuam sem badge (drag handle). Items do
// agent ganham um marker contextual.
const KIND_BADGE = {
  task: "✨",       // tarefa criada pelo agent
  reminder: "🔔",   // lembrete (vai pra Agenda mas mostra no Kanban)
  research: "🔍",   // pesquisa web já realizada (sempre na coluna Done)
};

function SortableTaskCard({ task, stage, onAdvance, onDelete, busy }) {
  // BUGFIX-KANBAN-DUAL-COLLECTION: tarefas vindas de db.tasks têm
  // source="agent". Drag-and-drop entre colunas escreveria via
  // /projects/tasks/{id} (legacy) que não conhece db.tasks. Pra evitar
  // edits silenciosos sem efeito, desabilitamos o drag e o delete pra
  // source=agent. Toggle "concluído" continua via /tasks/complete (rota
  // canônica do agent).
  const isAgent = task.source === "agent";
  const kind = task.kind || "task";
  const isResearch = kind === "research";  // pesquisa não pode ser "ressuscitada"
  const {
    attributes, listeners, setNodeRef, setActivatorNodeRef,
    transform, transition, isDragging,
  } = useSortable({
    id: task.id,
    data: { type: "task", stage, task },
    disabled: isAgent,
  });
  const isDone = stage === "done";

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "group flex items-center gap-2 px-3 py-2.5 rounded-lg border transition-colors bg-surface-card border-subtle hover:border-strong",
        isDragging && "opacity-40",
        isAgent && "border-brand-cyan/20",
      )}
      data-testid={`kanban-task-${task.id}`}
      data-source={task.source || "legacy"}
      {...attributes}
    >
      {isAgent ? (
        <span
          aria-hidden="true"
          title={
            kind === "reminder"
              ? "Lembrete — use o chat ou a Agenda pra alterar"
              : kind === "research"
                ? "Pesquisa já realizada"
                : "Tarefa do agente — use o chat pra mover entre colunas"
          }
          className="text-brand-cyan/70 text-[10px] select-none"
        >
          {KIND_BADGE[kind] || KIND_BADGE.task}
        </span>
      ) : (
        <button
          ref={setActivatorNodeRef}
          type="button"
          aria-label={`Arrastar ${task.title}`}
          className="opacity-0 group-hover:opacity-60 focus-visible:opacity-100 transition-opacity text-foreground-subtle cursor-grab active:cursor-grabbing touch-none"
          {...listeners}
        >
          <GripVertical className="w-3.5 h-3.5" />
        </button>
      )}
      <button
        type="button"
        onClick={() => onAdvance(task)}
        disabled={busy || isResearch || (isAgent && isDone)}
        aria-label={
          isResearch
            ? "Pesquisa concluída"
            : isAgent
              ? (isDone ? "Item concluído" : "Marcar como concluído")
              : "Avançar para próxima coluna"
        }
        title={
          isResearch
            ? "Pesquisa já realizada — sem ação"
            : isAgent && isDone
              ? (kind === "reminder"
                  ? "Use o chat pra reabrir este lembrete"
                  : "Use o chat pra reabrir esta tarefa")
              : undefined
        }
        className={cn(
          "shrink-0 w-5 h-5 rounded border flex items-center justify-center transition-all",
          isDone ? "bg-brand-green/20 border-brand-green/40" : "border-subtle hover:border-brand-cyan/60",
        )}
        data-testid={`kanban-task-advance-${task.id}`}
      >
        {isDone ? <span className="text-brand-green text-[10px]">✓</span> : null}
      </button>
      <span
        className={cn(
          "text-xs flex-1 min-w-0 truncate",
          isDone ? "text-foreground-subtle line-through" : "text-foreground",
        )}
      >
        {task.title}
      </span>
      {!isAgent && (
        <button
          type="button"
          onClick={() => onDelete(task)}
          aria-label={`Excluir ${task.title}`}
          className="p-1 rounded text-foreground-subtle hover:text-status-error hover:bg-status-error/10 opacity-0 group-hover:opacity-100 transition-all"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

// Card "ghost" durante drag (não-sortable, só visual)
function DragPreview({ task }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg border bg-surface-card border-brand-cyan/40 shadow-glow-cyan">
      <GripVertical className="w-3.5 h-3.5 text-brand-cyan" />
      <span className="text-xs text-foreground truncate">{task.title}</span>
    </div>
  );
}

// Coluna droppable (aceita drop quando vazia ou no espaço entre cards)
function ColumnDroppable({ stage, children }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `column:${stage.id}`,
    data: { type: "column", stage: stage.id },
  });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        "rounded-xl bg-surface-card border p-3 min-h-[180px] transition-colors",
        isOver ? "border-brand-cyan/40 bg-brand-cyan/[0.04]" : "border-subtle",
      )}
      data-testid={`kanban-column-${stage.id}`}
    >
      {children}
    </div>
  );
}

function NewTaskRow({ stage, onCreate }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!title.trim()) return;
    setSubmitting(true);
    try {
      await onCreate({ title: title.trim(), stage });
      setTitle("");
      setOpen(false);
    } finally { setSubmitting(false); }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full mt-2 px-3 py-2 rounded-lg border border-dashed border-subtle text-caption text-foreground-subtle hover:text-brand-cyan hover:border-brand-cyan/40 hover:bg-brand-cyan/5 transition-colors flex items-center justify-center gap-2"
        data-testid={`kanban-add-task-${stage}`}
      >
        <Plus className="w-3.5 h-3.5" />
        Tarefa
      </button>
    );
  }

  return (
    <div className="mt-2 flex items-center gap-2">
      <input
        autoFocus
        type="text" value={title} onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); submit(); }
          if (e.key === "Escape") { setOpen(false); setTitle(""); }
        }}
        onBlur={() => { if (!title.trim()) { setOpen(false); } }}
        placeholder="Nova tarefa…"
        className="flex-1 px-2.5 py-2 bg-surface-card border border-brand-cyan/40 rounded-md text-xs text-foreground placeholder-foreground-subtle focus:outline-none"
      />
      <button
        type="button"
        onClick={submit}
        disabled={!title.trim() || submitting}
        className="p-2 rounded-md bg-brand-cyan/15 border border-brand-cyan/40 text-brand-cyan hover:bg-brand-cyan/25 disabled:opacity-40"
        aria-label="Adicionar tarefa"
      >
        {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

export default function ProjetoKanban({ project, onTasksChange }) {
  const [tasks, setTasks] = useState(project.tasks || []);
  const [busyTaskId, setBusyTaskId] = useState(null);
  const [activeId, setActiveId] = useState(null);

  // Sincroniza quando o pai re-fetcha o projeto
  useEffect(() => { setTasks(project.tasks || []); }, [project.tasks, project.id]);

  const sensors = useSensors(
    // PointerSensor com delay pequeno: evita disparar drag em click acidental
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const stagesIds = STAGES.map(s => s.id);
  const nextStage = (s) => stagesIds[(stagesIds.indexOf(s) + 1) % stagesIds.length];

  const tasksByStage = useMemo(() => {
    const map = Object.fromEntries(stagesIds.map(id => [id, []]));
    for (const t of tasks) {
      const s = t.stage || "todo";
      if (!map[s]) map[s] = [];
      map[s].push(t);
    }
    return map;
  }, [tasks, stagesIds]);

  const updateTaskStage = useCallback(async (task, newStage) => {
    if (!task || task.stage === newStage) return;
    const isAgent = task.source === "agent";
    const kind = task.kind || "task";
    const before = tasks;
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, stage: newStage } : t));
    setBusyTaskId(task.id);
    try {
      let r;
      if (isAgent) {
        // BUGFIX-KANBAN-DUAL-COLLECTION + KANBAN-UNIFIED-VIEW (3.5):
        // items do agent só aceitam toggle "concluído", e a rota
        // varia por kind:
        //   task     → POST /api/tasks/complete
        //   reminder → POST /api/reminders/complete
        //   research → no-op (já vem como done; UI desabilita botão)
        if (newStage !== "done") {
          setTasks(before);
          toast.info("Itens do agente são só toggle de conclusão — use o chat pra outros stages");
          return;
        }
        if (kind === "research") {
          setTasks(before);
          toast.info("Pesquisa já está concluída");
          return;
        }
        const completeUrl =
          kind === "reminder"
            ? `${API_URL}/api/reminders/complete`
            : `${API_URL}/api/tasks/complete`;
        r = await authFetch(completeUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: task.id }),
        });
      } else {
        r = await authFetch(`${API_URL}/api/projects/tasks/${task.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ stage: newStage }),
        });
      }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      onTasksChange?.();
    } catch (e) {
      // Rollback otimista
      setTasks(before);
      toast.error("Erro ao mover tarefa");
    } finally { setBusyTaskId(null); }
  }, [tasks, onTasksChange]);

  const advanceTask = (task) => {
    // BUGFIX-KANBAN-DUAL-COLLECTION: agent items só fazem todo→done
    // (atalho do botão checkbox). Cycling completo (todo→doing→paused→done)
    // continua só pra legacy.
    if (task.source === "agent") {
      if (task.stage !== "done") updateTaskStage(task, "done");
      return;
    }
    updateTaskStage(task, nextStage(task.stage || "todo"));
  };

  const handleDelete = async (task) => {
    if (!window.confirm(`Excluir "${task.title}"?`)) return;
    const before = tasks;
    setTasks(prev => prev.filter(t => t.id !== task.id));
    try {
      const r = await authFetch(`${API_URL}/api/projects/tasks/${task.id}`, { method: "DELETE" });
      if (!r.ok) throw new Error("HTTP");
      onTasksChange?.();
    } catch (_) {
      setTasks(before);
      toast.error("Erro ao excluir tarefa");
    }
  };

  const handleCreate = async (data) => {
    try {
      const r = await authFetch(`${API_URL}/api/projects/${project.id}/tasks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!r.ok) throw new Error("HTTP");
      const t = await r.json();
      setTasks(prev => [...prev, t]);
      toast.success("Tarefa adicionada");
      onTasksChange?.();
    } catch (_) { toast.error("Erro ao criar tarefa"); }
  };

  // ── DnD handlers ──
  const handleDragStart = (event) => setActiveId(event.active.id);
  const handleDragCancel = () => setActiveId(null);

  const handleDragEnd = (event) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;
    const task = tasks.find(t => t.id === active.id);
    if (!task) return;

    // over.id pode ser:
    //   "column:{stage}" → drop em coluna vazia / fora de cards
    //   "{taskId}"        → drop em cima de outra task (lê stage da data)
    let targetStage = null;
    const overData = over.data?.current;
    if (overData?.type === "column" && overData?.stage) {
      targetStage = overData.stage;
    } else if (overData?.type === "task" && overData?.stage) {
      targetStage = overData.stage;
    } else if (typeof over.id === "string" && over.id.startsWith("column:")) {
      targetStage = over.id.slice("column:".length);
    }
    if (!targetStage || !stagesIds.includes(targetStage)) return;
    if (targetStage === task.stage) return;

    updateTaskStage(task, targetStage);
  };

  const activeTask = activeId ? tasks.find(t => t.id === activeId) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragCancel={handleDragCancel}
      onDragEnd={handleDragEnd}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid={`kanban-${project.id}`}>
        {STAGES.map(stage => {
          const stageTasks = tasksByStage[stage.id] || [];
          return (
            <ColumnDroppable key={stage.id} stage={stage}>
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="flex items-center gap-2">
                  <span className={cn("w-2 h-2 rounded-full", stage.dot)} aria-hidden="true" />
                  <span className={cn("text-caption font-medium", stage.color)}>{stage.label}</span>
                </div>
                <span className="text-micro text-foreground-subtle tabular-nums">{stageTasks.length}</span>
              </div>
              <SortableContext
                items={stageTasks.map(t => t.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-1.5">
                  <AnimatePresence>
                    {stageTasks.map(t => (
                      <SortableTaskCard
                        key={t.id}
                        task={t}
                        stage={stage.id}
                        onAdvance={advanceTask}
                        onDelete={handleDelete}
                        busy={busyTaskId === t.id}
                      />
                    ))}
                  </AnimatePresence>
                  <NewTaskRow stage={stage.id} onCreate={handleCreate} />
                </div>
              </SortableContext>
            </ColumnDroppable>
          );
        })}
      </div>
      <DragOverlay>
        {activeTask ? <DragPreview task={activeTask} /> : null}
      </DragOverlay>
    </DndContext>
  );
}
